import {
  ErrorResponse,
  UserManager,
  WebStorageStateStore,
  type User,
  type UserManagerSettings,
} from "oidc-client-ts";

import {
  PREVIEW_SIGNIN_CALLBACK_PATH,
  buildDirectCognitoLogoutUrl,
  buildPreviewLogoutStartUrl,
  createPreviewRouteHint,
  parsePreviewRelayConfig,
  type PreviewRelayConfig,
} from "../auth/previewRelay";
import type { AuthState } from "../auth/AuthContext";
import { SESSION_ENDED_MESSAGE, SessionEndedError } from "../auth/sessionEnded";
import {
  authCapabilities,
  identityFromProfile,
  type AuthIdentity,
} from "../auth/roles";

type Listener = () => void;

type RuntimeAppConfig = {
  enabled: boolean;
  authority: string;
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  scope: string;
  previewRelay: PreviewRelayConfig | null;
};

const DEFAULT_SCOPE = "openid email profile";
const INVALID_REFRESH_GRANT = "invalid_grant";
const OIDC_REQUEST_TIMEOUT_SECONDS = 15;
const ACCESS_TOKEN_RENEWAL_LEAD_SECONDS = 60;
const WORKSPACE_SESSION_TIMEOUT_MS = 8_000;
const listeners = new Set<Listener>();

type AuthSessionManager = Pick<
  UserManager,
  "getUser" | "removeUser" | "signinSilent" | "storeUser"
>;

/** Owns the persisted OIDC user and refreshes it before callers use a token. */
class AuthSession {
  private currentUser: User | null = null;
  private generation = 0;
  private renewal: Promise<User | null> | null = null;
  private renewalAccessToken: string | null = null;

  public constructor(
    private readonly manager: AuthSessionManager,
    private readonly onSessionEnded: () => void,
    private readonly renewalLockName: string,
  ) {}

  public hasUsableCredentials(): boolean {
    return isFreshUser(this.currentUser);
  }

  public user(): User | null {
    return this.currentUser;
  }

  public get version(): number {
    return this.generation;
  }

  public setUser(user: User): void {
    this.currentUser = user;
  }

  public replaceUser(user: User): void {
    this.generation += 1;
    this.currentUser = user;
  }

  public setUserFromManagerEvent(user: User): boolean {
    // signinSilent emits UserLoaded before its promise resolves. The renewal
    // owns accepting that user after checking the session generation; until
    // then, publishing the old expired user would incorrectly show sign-in.
    if (this.renewal) return false;
    this.currentUser = user;
    return true;
  }

  public clearUser(): void {
    this.currentUser = null;
  }

  public async restore(): Promise<User | null> {
    const generation = this.generation;
    const storedUser = await this.manager.getUser();
    if (generation !== this.generation) {
      await this.preserveCurrentSession();
      return null;
    }
    if (!storedUser) {
      this.currentUser = null;
      return null;
    }
    this.currentUser = storedUser;
    if (isReadyUser(storedUser)) {
      return storedUser;
    }
    if (!storedUser.refresh_token) {
      await this.discardEndedSession();
      return null;
    }
    return this.renew();
  }

  public renewExpiringUser(): Promise<User | null> {
    if (!this.currentUser?.refresh_token) {
      return Promise.resolve(this.currentUser);
    }
    return this.renew();
  }

  public async authorizationHeader(): Promise<string | null> {
    const currentUser = this.currentUser;
    const user =
      currentUser && isReadyUser(currentUser)
        ? currentUser
        : await this.restore();
    return user ? `Bearer ${user.access_token}` : null;
  }

  public async renewRejectedAuthorization(
    authorization: string,
  ): Promise<string | null> {
    if (!this.currentUser) return null;
    if (authorization !== `Bearer ${this.currentUser.access_token}`) {
      return this.authorizationHeader();
    }
    if (!this.currentUser.refresh_token) {
      await this.discardEndedSession();
      return null;
    }
    const user = await this.renew();
    return user ? `Bearer ${user.access_token}` : null;
  }

  public async discard(): Promise<void> {
    this.invalidate();
    await this.manager.removeUser();
  }

  public invalidate(): void {
    this.generation += 1;
    this.currentUser = null;
  }

  public async discardIfCurrentAuthorization(
    authorization: string | null,
  ): Promise<boolean> {
    if (
      this.renewal &&
      this.renewalAccessToken &&
      authorization === `Bearer ${this.renewalAccessToken}`
    ) {
      return false;
    }
    if (
      !authorization ||
      !this.currentUser ||
      authorization !== `Bearer ${this.currentUser.access_token}`
    ) {
      return false;
    }
    await this.discardEndedSession();
    return true;
  }

  private async renew(): Promise<User | null> {
    if (this.renewal) {
      return this.renewal;
    }
    const generation = this.generation;
    this.renewalAccessToken = this.currentUser?.access_token ?? null;
    const renewal = this.renewOnce(generation);
    this.renewal = renewal;
    try {
      return await renewal;
    } finally {
      if (this.renewal === renewal) {
        this.renewal = null;
        this.renewalAccessToken = null;
      }
    }
  }

  private async renewOnce(generation: number): Promise<User | null> {
    try {
      const renew = () => {
        // The session may have changed while another tab held the lock.
        if (generation !== this.generation) return Promise.resolve(null);
        return this.manager.signinSilent();
      };
      // Cognito rotates refresh tokens. A per-tab promise cannot prevent two
      // tabs from consuming the same stored token. Hold a same-origin lock
      // through signinSilent's read, refresh, and persistence of its successor.
      const renewedUser = navigator.locks
        ? await navigator.locks.request(
            this.renewalLockName,
            {
              signal: AbortSignal.timeout(OIDC_REQUEST_TIMEOUT_SECONDS * 1000),
            },
            renew,
          )
        : await renew();
      if (generation !== this.generation) {
        await this.preserveCurrentSession();
        return null;
      }
      if (isFreshUser(renewedUser)) {
        this.currentUser = renewedUser;
        return renewedUser;
      }
      await this.discardEndedSession();
      return null;
    } catch (error: unknown) {
      if (generation !== this.generation) {
        await this.preserveCurrentSession();
        return null;
      }
      if (isInvalidRefreshGrant(error)) {
        await this.discardEndedSession();
        return null;
      }
      throw error;
    }
  }

  private async preserveCurrentSession(): Promise<void> {
    if (this.currentUser) {
      await this.manager.storeUser(this.currentUser);
    } else {
      await this.manager.removeUser();
    }
  }

  private async discardEndedSession(): Promise<void> {
    this.onSessionEnded();
    await this.discard();
  }
}

let runtimeConfig: RuntimeAppConfig = {
  enabled: false,
  authority: "",
  clientId: "",
  redirectUri: defaultRedirectUri(),
  postLogoutRedirectUri: defaultPostLogoutRedirectUri(),
  scope: DEFAULT_SCOPE,
  previewRelay: null,
};
let manager: UserManager | null = null;
let session: AuthSession | null = null;
let initialization: Promise<void> | null = null;
let signInMessage: string | null = null;
let workspaceCanLaunch: boolean | null = null;
let workspaceCanAdminister: boolean | null = null;
let workspaceCanOperate: boolean | null = null;
let workspaceCanManageUsers: boolean | null = null;
let workspaceIsRegistered: boolean | null = null;
let workspaceIdentity: {
  subject: string;
  email?: string;
  displayName?: string;
} | null = null;

function isInternalFallbackEmail(email: string | undefined): boolean {
  return email?.toLowerCase().endsWith("@external.invalid") === true;
}

function mergedIdentity(
  workspace: AuthIdentity | null,
  token: AuthIdentity | null,
): AuthIdentity | null {
  if (!workspace) return token;
  const workspaceEmail = isInternalFallbackEmail(workspace.email)
    ? undefined
    : workspace.email;
  const email = workspaceEmail ?? token?.email;
  const displayName = workspace.displayName ?? token?.displayName;
  return {
    subject: workspace.subject,
    ...(email ? { email } : {}),
    ...(displayName ? { displayName } : {}),
  };
}

/** Load runtime Cognito config and the stored OIDC user once before rendering. */
export function initializeAuth(): Promise<void> {
  if (!initialization) {
    initialization = initializeAuthOnce().catch((error: unknown) => {
      initialization = null;
      manager = null;
      session = null;
      throw error;
    });
  }
  return initialization;
}

async function initializeAuthOnce(): Promise<void> {
  runtimeConfig = await loadRuntimeConfig();
  if (!runtimeConfig.enabled) {
    emit();
    return;
  }
  if (!runtimeConfig.authority || !runtimeConfig.clientId) {
    throw new Error(
      "Cognito auth is enabled but authority/client id is missing",
    );
  }

  const nextManager = new UserManager(userManagerSettings(runtimeConfig));
  const nextSession = new AuthSession(
    nextManager,
    () => {
      signInMessage = SESSION_ENDED_MESSAGE;
    },
    `fdr-session-renewal:${runtimeConfig.authority}:${runtimeConfig.clientId}`,
  );
  const emitIfCurrent = () => {
    if (session === nextSession) {
      emit();
    }
  };
  const unsubscribe = [
    nextManager.events.addUserLoaded((user) => {
      if (nextSession.setUserFromManagerEvent(user)) {
        emitIfCurrent();
      }
    }),
    nextManager.events.addUserUnloaded(() => {
      nextSession.clearUser();
      emitIfCurrent();
    }),
    nextManager.events.addAccessTokenExpired(() => {
      // The expiring event may already have an in-flight refresh, especially
      // when a sleeping tab wakes after both timers elapsed. Join that renewal
      // instead of briefly publishing signed-out and detaching every API
      // caller that could retry a transient refresh failure.
      void nextSession
        .renewExpiringUser()
        .then(emitIfCurrent)
        .catch(() => undefined);
    }),
  ];

  try {
    await nextSession.restore();
  } catch (error: unknown) {
    for (const stopListening of unsubscribe) {
      stopListening();
    }
    throw error;
  }
  nextManager.events.addAccessTokenExpiring(() => {
    // Keep transient failures retryable: request-time auth uses the same
    // single-flight renewal path when the next API call arrives.
    void nextSession
      .renewExpiringUser()
      .then(emitIfCurrent)
      .catch(() => undefined);
  });
  manager = nextManager;
  session = nextSession;
  emit();
}

function userManagerSettings(config: RuntimeAppConfig): UserManagerSettings {
  const storage = new WebStorageStateStore({ store: window.localStorage });
  const previewRelay = previewRelayForCurrentOrigin(config);
  return {
    authority: config.authority,
    client_id: config.clientId,
    redirect_uri: previewRelay
      ? `${previewRelay.origin}${PREVIEW_SIGNIN_CALLBACK_PATH}`
      : config.redirectUri,
    post_logout_redirect_uri: config.postLogoutRedirectUri,
    response_type: "code",
    scope: config.scope,
    requestTimeoutInSeconds: OIDC_REQUEST_TIMEOUT_SECONDS,
    automaticSilentRenew: false,
    stateStore: storage,
    userStore: storage,
  };
}

async function loadRuntimeConfig(): Promise<RuntimeAppConfig> {
  const envConfig = envRuntimeConfig();
  try {
    const response = await fetch("/auth-config.json", {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      throw new Error(`Auth configuration request failed: ${response.status}`);
    }
    const parsed: unknown = await response.json();
    validateRuntimeConfigDocument(parsed);
    return parseRuntimeConfig(parsed, envConfig);
  } catch (error: unknown) {
    // Deployed images always write this file. Treat its absence as an auth
    // outage, never as permission to enter the auth-disabled admin posture.
    if (import.meta.env.PROD) {
      throw error instanceof Error
        ? error
        : new Error("Auth configuration could not be loaded");
    }
    return envConfig;
  }
}

/**
 * The deployed file is an authorization control, not an optional preference.
 * Reject a syntactically valid but incomplete document before flexible
 * fallback parsing could turn it into the auth-disabled local posture.
 */
function validateRuntimeConfigDocument(value: unknown): void {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Auth configuration is malformed");
  }
  const record = value as Record<string, unknown>;
  if (typeof record.enabled !== "boolean") {
    throw new Error("Auth configuration must declare enabled as a boolean");
  }
  if (
    record.enabled &&
    (!stringValue(record.authority) || !stringValue(record.clientId))
  ) {
    throw new Error(
      "Enabled auth configuration must declare authority and clientId",
    );
  }
  if (record.previewRelay !== undefined) {
    parsePreviewRelayConfig(record.previewRelay);
  }
}

function envRuntimeConfig(): RuntimeAppConfig {
  const previewRelay = parsePreviewRelayConfig({
    enabled: import.meta.env.VITE_PREVIEW_AUTH_RELAY_ENABLED === "true",
    origin: import.meta.env.VITE_PREVIEW_AUTH_RELAY_ORIGIN || "",
    allowedHostPrefix:
      import.meta.env.VITE_PREVIEW_AUTH_ALLOWED_HOST_PREFIX || "",
    allowedHostSuffix:
      import.meta.env.VITE_PREVIEW_AUTH_ALLOWED_HOST_SUFFIX || "",
    logoutEndpoint: import.meta.env.VITE_COGNITO_LOGOUT_ENDPOINT || "",
  });
  return {
    enabled: import.meta.env.VITE_COGNITO_ENABLED === "true",
    authority: import.meta.env.VITE_COGNITO_AUTHORITY || "",
    clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || "",
    redirectUri:
      import.meta.env.VITE_COGNITO_REDIRECT_URI || defaultRedirectUri(),
    postLogoutRedirectUri:
      import.meta.env.VITE_COGNITO_POST_LOGOUT_REDIRECT_URI ||
      defaultPostLogoutRedirectUri(),
    scope: import.meta.env.VITE_COGNITO_SCOPE || DEFAULT_SCOPE,
    previewRelay,
  };
}

function parseRuntimeConfig(
  value: unknown,
  fallback: RuntimeAppConfig,
): RuntimeAppConfig {
  if (!value || typeof value !== "object") return fallback;
  const record = value as Record<string, unknown>;
  const authority = stringValue(record.authority) || fallback.authority;
  const clientId = stringValue(record.clientId) || fallback.clientId;
  const previewRelay =
    record.previewRelay === undefined
      ? fallback.previewRelay
      : parsePreviewRelayConfig(record.previewRelay);
  return {
    enabled: booleanValue(record.enabled) ?? fallback.enabled,
    authority,
    clientId,
    redirectUri:
      stringValue(record.redirectUri) ||
      fallback.redirectUri ||
      defaultRedirectUri(),
    postLogoutRedirectUri:
      stringValue(record.postLogoutRedirectUri) ||
      fallback.postLogoutRedirectUri ||
      defaultPostLogoutRedirectUri(),
    scope: stringValue(record.scope) || fallback.scope || DEFAULT_SCOPE,
    previewRelay,
  };
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function booleanValue(value: unknown): boolean | null {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    if (value.toLowerCase() === "true") return true;
    if (value.toLowerCase() === "false") return false;
  }
  return null;
}

function defaultRedirectUri(): string {
  return `${browserOrigin()}/auth/callback`;
}

function defaultPostLogoutRedirectUri(): string {
  return browserOrigin();
}

function browserOrigin(): string {
  return typeof window === "undefined"
    ? "http://localhost"
    : window.location.origin;
}

function isFreshUser(user: User | null): boolean {
  return !!user && !!user.access_token && !user.expired;
}

function isReadyUser(user: User): boolean {
  return (
    isFreshUser(user) &&
    (!user.refresh_token ||
      user.expires_in === undefined ||
      user.expires_in > ACCESS_TOKEN_RENEWAL_LEAD_SECONDS)
  );
}

function isInvalidRefreshGrant(error: unknown): boolean {
  return (
    error instanceof ErrorResponse && error.error === INVALID_REFRESH_GRANT
  );
}

function requireManager(): UserManager {
  if (!manager) throw new Error("Cognito auth has not been initialized");
  return manager;
}

export function isAuthEnabled(): boolean {
  return runtimeConfig.enabled;
}

/** Current typed identity and effective workspace permissions. */
export function authState(): AuthState {
  if (!runtimeConfig.enabled) {
    return {
      configured: false,
      authenticated: true,
      identity: { subject: "local-development", displayName: "Local admin" },
      roles: ["super_admin"],
      isRegistered: true,
      ...authCapabilities(false, []),
      canManageUsers: false,
    };
  }
  const user = session?.user() ?? null;
  const authenticated = isFreshUser(user);
  const tokenIdentity = authenticated
    ? identityFromProfile(user?.profile)
    : null;
  const identity = authenticated
    ? mergedIdentity(workspaceIdentity, tokenIdentity)
    : null;
  const roles = identity
    ? workspaceCanOperate === true
      ? (["super_admin"] as const)
      : workspaceCanAdminister === true
        ? (["admin"] as const)
        : workspaceCanLaunch === true
          ? (["launcher"] as const)
          : []
    : [];
  return {
    configured: true,
    authenticated,
    identity,
    roles,
    isRegistered: workspaceIsRegistered === true,
    ...authCapabilities(true, roles),
    canManageUsers: workspaceCanManageUsers === true,
  };
}

/** Human-readable reason the user was returned to the sign-in screen. */
export function authSignInMessage(): string | null {
  return signInMessage;
}

export async function refreshWorkspaceSession(): Promise<void> {
  if (!runtimeConfig.enabled) return;
  const authorization = await authHeader();
  if (!authorization) {
    workspaceCanLaunch = null;
    workspaceCanAdminister = null;
    workspaceCanOperate = null;
    workspaceCanManageUsers = null;
    workspaceIsRegistered = null;
    workspaceIdentity = null;
    return;
  }
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(
    () => controller.abort(),
    WORKSPACE_SESSION_TIMEOUT_MS,
  );
  try {
    const response = await authenticatedFetch("/api/v1/workspace/session", {
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`Workspace session failed: ${response.status}`);
    }
    let record = workspaceSessionRecord(await response.json());
    if (!record.is_registered && record.can_launch) {
      const registration = await authenticatedFetch(
        "/api/v1/workspace/registration",
        {
          method: "POST",
          signal: controller.signal,
        },
      );
      if (!registration.ok) {
        throw new Error(
          `Workspace registration failed: ${registration.status}`,
        );
      }
      record = workspaceSessionRecord(await registration.json());
      if (!record.is_registered) {
        throw new Error("Workspace registration did not complete");
      }
    }
    workspaceCanLaunch = record.can_launch;
    workspaceCanAdminister = record.can_administer;
    workspaceCanOperate = record.can_operate === true;
    workspaceCanManageUsers = record.can_manage_users;
    workspaceIsRegistered = record.is_registered;
    const rawIdentity = record.identity;
    if (rawIdentity && typeof rawIdentity === "object") {
      const item = rawIdentity as Record<string, unknown>;
      workspaceIdentity =
        typeof item.subject === "string"
          ? {
              subject: item.subject,
              ...(typeof item.email === "string" ? { email: item.email } : {}),
              ...(typeof item.display_name === "string"
                ? { displayName: item.display_name }
                : {}),
            }
          : null;
    }
    emit();
  } catch (error: unknown) {
    if (controller.signal.aborted) {
      throw new Error("Workspace session timed out");
    }
    throw error;
  } finally {
    globalThis.clearTimeout(timeout);
  }
}

function workspaceSessionRecord(payload: unknown): Record<string, unknown> & {
  can_launch: boolean;
  can_administer: boolean;
  can_operate?: boolean;
  can_manage_users: boolean;
  is_registered: boolean;
} {
  if (!payload || typeof payload !== "object") {
    throw new Error("Workspace session is invalid");
  }
  const record = payload as Record<string, unknown>;
  if (
    typeof record.can_launch !== "boolean" ||
    typeof record.can_administer !== "boolean" ||
    (record.can_operate !== undefined &&
      typeof record.can_operate !== "boolean") ||
    typeof record.can_manage_users !== "boolean" ||
    typeof record.is_registered !== "boolean"
  ) {
    throw new Error("Workspace session permissions are invalid");
  }
  return record as Record<string, unknown> & {
    can_launch: boolean;
    can_administer: boolean;
    can_manage_users: boolean;
    is_registered: boolean;
  };
}

/** `Authorization` header value for API requests, if Cognito auth is active. */
export async function authHeader(): Promise<string | null> {
  if (!runtimeConfig.enabled) {
    return null;
  }
  const wasUsable = session?.hasUsableCredentials() ?? false;
  const header = (await session?.authorizationHeader()) ?? null;
  if (!wasUsable && session?.hasUsableCredentials()) {
    emit();
  }
  return header;
}

export async function authenticatedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const url = new URL(
    input instanceof Request ? input.url : String(input),
    browserOrigin(),
  );
  if (url.origin !== browserOrigin() || !url.pathname.startsWith("/api/")) {
    throw new Error(
      "Authenticated requests must use the same-origin API proxy.",
    );
  }
  const activeSession = session;
  const version = activeSession?.version;
  const requireCurrentSession = () => {
    if (
      runtimeConfig.enabled &&
      (!activeSession ||
        activeSession !== session ||
        activeSession.version !== version)
    ) {
      throw new SessionEndedError();
    }
  };
  const authorization = await authHeader();
  requireCurrentSession();
  if (runtimeConfig.enabled && !authorization) {
    throw new SessionEndedError();
  }
  const headers = Object.fromEntries(
    new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    ),
  );
  if (authorization) {
    delete headers.authorization;
    headers.Authorization = authorization;
  }
  const retryInput = input instanceof Request ? input.clone() : input;
  const response = await fetch(input, { ...init, headers });
  requireCurrentSession();
  if (response.status !== 401 || !authorization || !activeSession) {
    return response;
  }
  const signal =
    init?.signal ?? (input instanceof Request ? input.signal : undefined);
  signal?.throwIfAborted();
  const renewedAuthorization =
    await activeSession.renewRejectedAuthorization(authorization);
  emit();
  requireCurrentSession();
  if (!renewedAuthorization) {
    throw new SessionEndedError();
  }
  signal?.throwIfAborted();
  const retryHeaders = { ...headers, Authorization: renewedAuthorization };
  const retried = await fetch(retryInput, { ...init, headers: retryHeaders });
  requireCurrentSession();
  if (retried.status === 401) {
    await clearRejectedCredentials(renewedAuthorization);
    throw new SessionEndedError();
  }
  return retried;
}

/** Start the hosted-UI Authorization Code + PKCE sign-in flow. */
export async function signIn(returnTo: string): Promise<void> {
  await initializeAuth();
  signInMessage = null;
  const previewRelay = previewRelayForCurrentOrigin(runtimeConfig);
  await requireManager().signinRedirect({
    state: { returnTo },
    ...(previewRelay
      ? {
          url_state: createPreviewRouteHint(
            window.location.origin,
            previewRelay,
          ),
        }
      : {}),
  });
}

/** Finish the hosted-UI redirect and return the same-origin route to restore. */
export async function completeSignIn(callbackUrl: string): Promise<string> {
  await initializeAuth();
  const currentManager = requireManager();
  const user = await currentManager.signinRedirectCallback(callbackUrl);
  signInMessage = null;
  session?.replaceUser(user);
  await currentManager.storeUser(user);
  emit();
  return returnToFromState(user.state);
}

function returnToFromState(state: unknown): string {
  if (!state || typeof state !== "object") return "/";
  const returnTo = (state as { returnTo?: unknown }).returnTo;
  if (typeof returnTo !== "string") return "/";
  return returnTo.startsWith("/") && !returnTo.startsWith("//")
    ? returnTo
    : "/";
}

/** Clear the local OIDC user without a provider logout redirect. */
function clearCredentials(): void {
  signInMessage = null;
  const discard = session?.discard();
  emit();
  if (discard) {
    void discard.finally(emit);
  }
}

/** Clear only when the rejected request used the still-current access token. */
export async function clearRejectedCredentials(
  authorization: string | null,
): Promise<void> {
  await session?.discardIfCurrentAuthorization(authorization);
  emit();
}

/** Sign out locally and redirect through Cognito logout when configured. */
export async function signOut(): Promise<void> {
  await initializeAuth();
  if (!runtimeConfig.enabled) {
    clearCredentials();
    return;
  }
  const currentManager = requireManager();
  const previewRelay = previewRelayForCurrentOrigin(runtimeConfig);
  signInMessage = null;
  session?.invalidate();
  emit();
  if (previewRelay) {
    await currentManager.removeUser();
    window.location.assign(
      buildPreviewLogoutStartUrl(window.location.origin, previewRelay),
    );
    return;
  }
  const logoutEndpoint =
    runtimeConfig.previewRelay?.logoutEndpoint ??
    (await currentManager.metadataService.getEndSessionEndpoint());
  if (!logoutEndpoint) throw new Error("Cognito logout endpoint is missing");
  await currentManager.removeUser();
  window.location.assign(
    buildDirectCognitoLogoutUrl(
      logoutEndpoint,
      runtimeConfig.clientId,
      runtimeConfig.postLogoutRedirectUri,
    ),
  );
}

/** Subscribe to auth changes (set/clear/expiry); returns an unsubscribe fn. */
export function subscribeAuth(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function emit(): void {
  for (const fn of listeners) fn();
}

function previewRelayForCurrentOrigin(
  config: RuntimeAppConfig,
): PreviewRelayConfig | null {
  const previewRelay = config.previewRelay;
  if (!previewRelay || window.location.origin === previewRelay.origin) {
    return null;
  }
  createPreviewRouteHint(window.location.origin, previewRelay);
  return previewRelay;
}
